/**
 * The persisted output of the gap-analysis orchestrators, as one document per
 * SCOPE — a delivery project or a sales deal, keyed by its `external_id` in
 * `canonical_records`.
 *
 * This replaces the Notion report as the artifact each orchestrator produces:
 * the same material, stored against the thing it is about instead of a page
 * tree. A scope document ({@link GapAnalysisScope}) accumulates every execution
 * ever run against that scope in `runs[]`, so reading one scope is one lookup
 * with no join and no cross-run assembly.
 *
 * One EXECUTION — the whole find⇄validate loop, up to three runs, not a single
 * Workflow invocation — is one {@link GapAnalysisRun} entry in each scope
 * document it touched. Its run-level data (iterations, stop reason, overview,
 * consistency pass) is copied VERBATIM into every one of them; only `gaps`,
 * `candidates` and `coverage` differ. That duplication is deliberate: it is what
 * lets a scope be read alone. `executionId` is what stitches the copies back
 * together when a reader wants the execution as a whole.
 *
 * Within a run entry the material is split in three so the gaps are easy to read
 * on their own: {@link GapAnalysisRun.gaps} is every gap the execution
 * adjudicated for this scope; {@link GapAnalysisRun.candidates} is the pre-merge
 * finder proposals those gaps were built from, so the merge stays auditable; and
 * {@link GapAnalysisRun.coverage} is the scope's own bookkeeping — what each
 * finder covered and what could not be adjudicated. The gaps themselves are
 * {@link RunGap} (see `gap-run.domain.ts`).
 *
 * Delivery and sales share one document and one run shape. The only variant
 * split is at the gap level ({@link RunGap}) and inside `coverage`, where the
 * sales pipeline's extra bookkeeping — deal eligibility, procurement sub-path,
 * suppressed candidates, the cross-batch consistency pass — is grouped into one
 * optional {@link SalesScopeDetails} member rather than spread across the shared
 * coverage as loose optional keys. `variant` on the document says which pipeline
 * produced it (and thus whether `sales` is populated).
 *
 * Enum values are UPPERCASE, matching the rest of the run contract. The one
 * exception is `variant` ({@link GapCategory}), whose values are lowercase
 * (`"delivery"` / `"sales"`).
 */
// ---------------------------------------------------------------------------
// Loop
// ---------------------------------------------------------------------------
/** Why the find⇄validate loop stopped. The loop is capped at three runs. */
export var GapAnalysisStopReason;
(function (GapAnalysisStopReason) {
    /** The missed sources validators reported could not enable a new gap. */
    GapAnalysisStopReason["NO_QUALIFYING_SOURCES"] = "NO_QUALIFYING_SOURCES";
    /** The last run produced no new gaps. */
    GapAnalysisStopReason["NO_NEW_GAPS"] = "NO_NEW_GAPS";
    GapAnalysisStopReason["MAX_RUNS_REACHED"] = "MAX_RUNS_REACHED";
})(GapAnalysisStopReason || (GapAnalysisStopReason = {}));
/** Which finder lens produced a per-family note. */
export var GapAnalysisFinderFamily;
(function (GapAnalysisFinderFamily) {
    GapAnalysisFinderFamily["COMMON_SENSE"] = "COMMON_SENSE";
    GapAnalysisFinderFamily["ANALOG"] = "ANALOG";
})(GapAnalysisFinderFamily || (GapAnalysisFinderFamily = {}));
/**
 * State of the deal-wide pass that reconciles verdicts across validator
 * batches. Sales only; delivery validates in batches and never re-adjudicates.
 */
export var GapAnalysisConsistencyStatus;
(function (GapAnalysisConsistencyStatus) {
    /** Zero or one usable batch — nothing to reconcile. */
    GapAnalysisConsistencyStatus["NOT_REQUIRED"] = "NOT_REQUIRED";
    GapAnalysisConsistencyStatus["PENDING"] = "PENDING";
    GapAnalysisConsistencyStatus["COMPLETED"] = "COMPLETED";
    /** The pass failed; every gap it covered stays pending. */
    GapAnalysisConsistencyStatus["FAILED"] = "FAILED";
})(GapAnalysisConsistencyStatus || (GapAnalysisConsistencyStatus = {}));
// ---------------------------------------------------------------------------
// Sales-only scope state
// ---------------------------------------------------------------------------
/**
 * Whether the deal is a live opportunity worth analyzing. An ineligible or
 * unresolved deal keeps its coverage entry but has its candidates suppressed
 * rather than validated.
 */
export var GapAnalysisEligibilityStatus;
(function (GapAnalysisEligibilityStatus) {
    GapAnalysisEligibilityStatus["ELIGIBLE"] = "ELIGIBLE";
    GapAnalysisEligibilityStatus["INELIGIBLE"] = "INELIGIBLE";
    /** The two finder families disagreed. */
    GapAnalysisEligibilityStatus["UNRESOLVED"] = "UNRESOLVED";
})(GapAnalysisEligibilityStatus || (GapAnalysisEligibilityStatus = {}));
/** The procurement sub-path a deal follows, established in Step 0. */
export var GapAnalysisProcurementPath;
(function (GapAnalysisProcurementPath) {
    GapAnalysisProcurementPath["ANNOUNCED_PUBLIC_TENDER"] = "ANNOUNCED_PUBLIC_TENDER";
    GapAnalysisProcurementPath["PRE_TENDER"] = "PRE_TENDER";
    GapAnalysisProcurementPath["FRAMEWORK_OR_CPO"] = "FRAMEWORK_OR_CPO";
    GapAnalysisProcurementPath["SMALL_VALUE_PUBLIC"] = "SMALL_VALUE_PUBLIC";
    GapAnalysisProcurementPath["UNKNOWN"] = "UNKNOWN";
})(GapAnalysisProcurementPath || (GapAnalysisProcurementPath = {}));
// ---------------------------------------------------------------------------
// Pre-merge candidates
// ---------------------------------------------------------------------------
/** What became of a finder candidate at the merge stage. */
export var GapCandidateDisposition;
(function (GapCandidateDisposition) {
    /** Became a gap on its own. */
    GapCandidateDisposition["PROMOTED"] = "PROMOTED";
    /** Merged with another family's candidate into one gap. */
    GapCandidateDisposition["MERGED"] = "MERGED";
    /**
     * Never reached adjudication: killed by the finder's do-not-report filter, or
     * suppressed wholesale because its scope was ruled ineligible (sales).
     */
    GapCandidateDisposition["FILTERED"] = "FILTERED";
    /** Dropped at the merge stage. */
    GapCandidateDisposition["DISCARDED"] = "DISCARDED";
})(GapCandidateDisposition || (GapCandidateDisposition = {}));
